import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { settingsApi } from '../../api/settings';
import {
  CANT_ESTIMATE_RATES_LINE,
  PAID_ACTIONS,
  estimateChatActionUsd,
  estimatePaidActionCostUsd,
  isChatSessionAction,
  formatChatSendEstimateLine,
  formatPaidEstimateAmount,
  formatPaidEstimateLine,
  pricingFromCatalog,
  resolvePaidModelId,
} from '../../utils/estimatePaidActionCost';
import './PaidActionConfirm.css';

/**
 * Quiet cost chip for the chat header, immediately left of the model
 * picker. Spendable chat only. The full sentence lives on the label;
 * the confirm dialog keeps that sentence in the dialog body.
 * A model change clears the previous dollars before the next band lands.
 * A longer thread keeps the last amount until the next band lands.
 */
export default function PaidActionEstimate({
  actionId = 'chat_message',
  inputTokens = null,
  modelId = null,
  messageCount = null,
  discovery = false,
}) {
  const action = PAID_ACTIONS[actionId];
  const explicitModelId = typeof modelId === 'string' && modelId.trim() ? modelId.trim() : '';
  const requestKey = `${actionId}|${inputTokens ?? ''}|${explicitModelId}|${messageCount ?? ''}|${discovery ? 1 : 0}`;
  const preserveKey = `${actionId}|${explicitModelId}|${discovery ? 1 : 0}`;
  const [view, setView] = useState({
    key: '',
    phase: 'loading',
    amount: '',
    detail: 'Loading estimate',
  });
  const lastReadyRef = useRef(null);
  const chipRef = useRef(null);
  const densityRef = useRef('full');
  const rowWidthRef = useRef(0);
  const [density, setDensity] = useState('full');

  useEffect(() => {
    if (!action) return undefined;
    let cancelled = false;
    const key = requestKey;
    const held = lastReadyRef.current;
    const keep = Boolean(
      held
      && held.preserveKey === preserveKey
      && held.view?.phase === 'ready'
      && held.view.amount,
    );
    if (!keep) {
      setView({ key, phase: 'loading', amount: '', detail: 'Loading estimate' });
    }
    (async () => {
      try {
        const [settingsRes, catalogRes] = await Promise.all([
          settingsApi.getSettings(),
          settingsApi.getOpenRouterCatalog().catch(() => null),
        ]);
        if (cancelled) return;
        const settings = settingsRes?.data || settingsRes || {};
        const id = explicitModelId || resolvePaidModelId(settings?.model_preferences, action);
        const models = catalogRes?.data?.models || catalogRes?.models || [];
        const pricing = pricingFromCatalog(models, id);
        const size = inputTokens != null ? inputTokens : action.planningInputTokens;
        const sessionAction = isChatSessionAction(actionId);
        const band = sessionAction
          ? estimateChatActionUsd({
            actionId,
            catalog: models,
            modelPreferences: settings?.model_preferences,
            inputTokens: size,
            replyModelId: id,
            messageCount,
            discovery,
          })
          : estimatePaidActionCostUsd(pricing, action, size);
        const amount = formatPaidEstimateAmount(band);
        const detail = sessionAction
          ? formatChatSendEstimateLine(band)
          : formatPaidEstimateLine(band);
        if (cancelled) return;
        if (!amount || !detail) {
          if (keep) {
            setView({ ...held.view, key });
            return;
          }
          setView({ key, phase: 'missing', amount: '', detail: CANT_ESTIMATE_RATES_LINE });
          return;
        }
        const next = { key, phase: 'ready', amount, detail };
        lastReadyRef.current = { preserveKey, view: next };
        setView(next);
      } catch {
        if (cancelled) return;
        if (keep) {
          setView({ ...held.view, key });
          return;
        }
        setView({ key, phase: 'missing', amount: '', detail: CANT_ESTIMATE_RATES_LINE });
      }
    })();
    return () => { cancelled = true; };
  }, [action, actionId, discovery, explicitModelId, inputTokens, messageCount, preserveKey, requestKey]);

  const heldNow = lastReadyRef.current;
  const preserved = (
    heldNow
    && heldNow.preserveKey === preserveKey
    && heldNow.view?.phase === 'ready'
    && heldNow.view.amount
  ) ? heldNow.view : null;
  const current = view.key === requestKey
    ? view
    : (preserved || { phase: 'loading', amount: '', detail: 'Loading estimate' });

  useLayoutEffect(() => {
    const chip = chipRef.current;
    if (!chip) return undefined;
    const row = chip.closest('.chat-interface__header-badges')
      || chip.closest('.chat-pane__phone-controls');
    if (!row || typeof ResizeObserver === 'undefined') return undefined;

    const fit = () => {
      const width = row.clientWidth;
      if (!(width > 0)) return;
      const grew = width > rowWidthRef.current + 4;
      rowWidthRef.current = width;

      const measure = (name) => {
        chip.hidden = name === 'hidden';
        chip.dataset.density = name;
        return row.scrollWidth;
      };

      let next = grew ? 'full' : densityRef.current;
      if (!(next === 'hidden' && !grew)) {
        const scroll = measure(next === 'hidden' ? 'full' : next);
        if (scroll > width + 1) {
          if (next === 'full') {
            next = 'amount';
            if (measure('amount') > width + 1) next = 'hidden';
          } else {
            next = 'hidden';
          }
        }
      }
      measure(next);
      if (next !== densityRef.current) {
        densityRef.current = next;
        setDensity(next);
      }
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(row);
    return () => observer.disconnect();
  }, [current.phase, current.amount, current.detail]);

  if (!action) return null;

  const label = current.detail;

  return (
    <span
      ref={chipRef}
      className="chat-estimate"
      data-testid="paid-action-inline-estimate"
      data-density={density}
      hidden={density === 'hidden'}
      title={label}
      aria-label={label}
    >
      {current.phase === 'loading' ? (
        <span className="chat-estimate__loading" aria-hidden="true">…</span>
      ) : current.phase === 'missing' ? (
        <span className="chat-estimate__missing">Can&apos;t estimate</span>
      ) : (
        <>
          <span className="chat-estimate__phrase">
            {String(current.amount).startsWith('under ') ? null : (
              <span className="chat-estimate__about">About </span>
            )}
            <span className="chat-estimate__amount">{current.amount}</span>
          </span>
        </>
      )}
    </span>
  );
}
